export type DiagnosticLevel = "PASS" | "FAIL" | "INFO" | "LIMITATION";

export type DiagnosticEntry = {
  id: string;
  timestamp: string;
  sessionId: string;
  level: DiagnosticLevel;
  category: "aqua" | "operator" | "application" | "network" | "device";
  event: string;
  message: string;
  action?: string;
  target?: string;
  view?: string;
  expected?: string;
  actual?: string;
};

export type DiagnosticRange = "today" | "seven_days" | "all";

const STORAGE_KEY = "aquadraw:aqua:diagnostics:v1";
const SESSION_KEY = "aquadraw:aqua:diagnostic-session:v1";
const EVENT_NAME = "aquadraw:diagnostics-updated";
const MAX_ENTRIES = 2500;
const RETENTION_MS = 30 * 24 * 60 * 60 * 1000;

let memoryEntries: DiagnosticEntry[] = [];

function id() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function diagnosticSessionId() {
  if (typeof window === "undefined") return "automated-test";
  let value = window.sessionStorage.getItem(SESSION_KEY);
  if (!value) {
    value = id();
    window.sessionStorage.setItem(SESSION_KEY, value);
  }
  return value;
}

export function sanitizeDiagnosticText(input: unknown, maxLength = 700) {
  return String(input ?? "")
    .replace(/\bsk-[A-Za-z0-9_-]{12,}\b/g, "[REDACTED_API_KEY]")
    .replace(/Bearer\s+[A-Za-z0-9._~+\/-]{12,}/gi, "Bearer [REDACTED]")
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[REDACTED_EMAIL]")
    .replace(/(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g, "[REDACTED_PHONE]")
    .replace(/data:image\/[a-z0-9.+-]+;base64,[A-Za-z0-9+/=]+/gi, "[REDACTED_PHOTO]")
    .slice(0, maxLength);
}

function load(): DiagnosticEntry[] {
  if (typeof window === "undefined") return memoryEntries;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]") as DiagnosticEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function save(entries: DiagnosticEntry[]) {
  memoryEntries = entries;
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {
    // Diagnostics must never crash the host application.
  }
}

export function recordDiagnostic(input: Omit<DiagnosticEntry, "id" | "timestamp" | "sessionId" | "message"> & { message: unknown }) {
  const cutoff = Date.now() - RETENTION_MS;
  const entry: DiagnosticEntry = {
    ...input,
    id: id(),
    timestamp: new Date().toISOString(),
    sessionId: diagnosticSessionId(),
    message: sanitizeDiagnosticText(input.message),
    action: input.action ? sanitizeDiagnosticText(input.action, 120) : undefined,
    target: input.target ? sanitizeDiagnosticText(input.target, 160) : undefined,
    view: input.view ? sanitizeDiagnosticText(input.view, 80) : undefined,
    expected: input.expected ? sanitizeDiagnosticText(input.expected, 300) : undefined,
    actual: input.actual ? sanitizeDiagnosticText(input.actual, 300) : undefined,
  };
  const entries = [...load().filter((item) => new Date(item.timestamp).getTime() >= cutoff), entry].slice(-MAX_ENTRIES);
  save(entries);
  return entry;
}

export function readDiagnostics(range: DiagnosticRange = "all") {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const cutoff = range === "today" ? startToday : range === "seven_days" ? Date.now() - 7 * 24 * 60 * 60 * 1000 : 0;
  return load().filter((item) => new Date(item.timestamp).getTime() >= cutoff);
}

export function clearDiagnostics() {
  save([]);
}

export function subscribeDiagnostics(listener: () => void) {
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}

export function installGlobalDiagnostics() {
  if (typeof window === "undefined") return () => undefined;
  const onError = (event: ErrorEvent) => recordDiagnostic({
    level: "FAIL",
    category: "application",
    event: "window.error",
    message: event.message || "Unhandled application error.",
    actual: event.error instanceof Error ? event.error.name : "Error",
  });
  const onRejection = (event: PromiseRejectionEvent) => recordDiagnostic({
    level: "FAIL",
    category: "application",
    event: "promise.unhandled",
    message: event.reason instanceof Error ? event.reason.message : event.reason,
  });
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  recordDiagnostic({ level: "INFO", category: "device", event: "app.session.started", message: "Aqua Draw diagnostic session started." });
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}

export function buildDiagnosticReport(entries: DiagnosticEntry[], range: DiagnosticRange, build = "Aqua Draw 0.19.0-invoice-holdbacks") {
  const counts = entries.reduce((result, entry) => ({ ...result, [entry.level]: result[entry.level] + 1 }), { PASS: 0, FAIL: 0, INFO: 0, LIMITATION: 0 } as Record<DiagnosticLevel, number>);
  const device = typeof navigator === "undefined" ? "Automated test runtime" : sanitizeDiagnosticText(`${navigator.userAgent}; language=${navigator.language}`, 500);
  const viewport = typeof window === "undefined" ? "unknown" : `${window.innerWidth}x${window.innerHeight}; pixelRatio=${window.devicePixelRatio}`;
  const lines = entries.map((entry) => [
    `[${entry.timestamp}] ${entry.level} ${entry.category}/${entry.event}`,
    entry.action ? `action=${entry.action}` : "",
    entry.target ? `target=${entry.target}` : "",
    entry.view ? `view=${entry.view}` : "",
    entry.expected ? `expected=${entry.expected}` : "",
    entry.actual ? `actual=${entry.actual}` : "",
    `message=${entry.message}`,
  ].filter(Boolean).join(" | "));

  return [
    "AQUA DRAW DIAGNOSTIC REPORT",
    `Generated: ${new Date().toISOString()}`,
    `Range: ${range}`,
    `Build: ${build}`,
    `Physical voice status: NOT TESTED until Dave approves device behavior`,
    `Device: ${device}`,
    `Viewport: ${viewport}`,
    `Events: ${entries.length} | PASS ${counts.PASS} | FAIL ${counts.FAIL} | LIMITATION ${counts.LIMITATION} | INFO ${counts.INFO}`,
    "Privacy: API keys, bearer tokens, emails, phone numbers, and photo data are redacted.",
    "",
    "EVENT LOG",
    ...(lines.length ? lines : ["No diagnostic events in this range."]),
  ].join("\n");
}
